import { Link, useNavigate } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronDown, BookOpen, Settings2 } from "lucide-react";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SidebarEditor } from "@/components/SidebarEditor";

function Items({ enc, groups }) {
  const navigate = useNavigate();
  const accent = enc === "ghoulax" ? "ghoulax-accent" : "zyfley-accent";
  const goRandom = async () => {
    try { const { data } = await api.get("/random"); navigate(`/article/${data.slug}`); } catch (e) { /* random fetch failed; keep user on current page */ }
  };
  return (
    <nav className="space-y-5">
      {groups.map((section) => (
        <div key={section.id}>
          <h4 className={`mb-1.5 text-xs font-bold uppercase tracking-wider ${accent}`}>{section.label}</h4>
          <ul className="space-y-0.5 border-l pl-3" style={{ borderLeftColor: "var(--arc-border)" }}>
            {section.items.map((it) => (
              <li key={it.id}>
                {it.random ? (
                  <button data-testid="sidebar-random" onClick={goRandom} className="block w-full text-left text-sm hover:underline" style={{ color: "var(--arc-text-2)" }}>
                    {it.label}
                  </button>
                ) : (it.to || "").startsWith("http") ? (
                  <a href={it.to} target="_blank" rel="noreferrer" className="block text-sm hover:underline" style={{ color: "var(--arc-text-2)" }}>{it.label}</a>
                ) : (
                  <Link to={(it.to || "").startsWith("/article/") ? `${it.to}?enc=${enc}` : (it.to || "#")} className="block text-sm hover:underline" style={{ color: "var(--arc-text-2)" }}>{it.label}</Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export default function EncyclopediaSidebar({ enc }) {
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [cfg, setCfg] = useState(null);

  const load = () => api.get(`/nav/${enc}`).then((r) => setCfg(r.data)).catch(() => setCfg({ hidden: false, groups: [] }));
  useEffect(() => { load(); }, [enc]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cfg) return null;
  if (cfg.hidden && !isAdmin) return null;

  const EditButton = (
    isAdmin && (
      <button onClick={() => setEditOpen(true)} data-testid={`sidebar-edit-${enc}`} className="mb-3 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold opacity-0 transition-opacity group-hover:opacity-100" style={{ backgroundColor: "var(--arc-card)" }}>
        <Settings2 size={13} /> Edit sidebar
      </button>
    )
  );

  const EditDialog = (
    <Dialog open={editOpen} onOpenChange={setEditOpen}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" style={{ backgroundColor: "var(--arc-bg)" }}>
        <DialogHeader><DialogTitle>Customise the {enc === "ghoulax" ? "Ghoulax" : "Zyfley"} sidebar</DialogTitle></DialogHeader>
        <SidebarEditor enc={enc} onSaved={() => { load(); setEditOpen(false); }} />
      </DialogContent>
    </Dialog>
  );

  // Admin sees an editor entry point even when hidden
  if (cfg.hidden && isAdmin) {
    return (
      <aside className="hidden w-56 shrink-0 lg:block">
        <div className="sticky top-20 rounded-md border border-dashed p-3 text-xs" style={{ color: "var(--arc-text-2)" }}>
          Sidebar is hidden from visitors.
          <div className="mt-2"><button onClick={() => setEditOpen(true)} data-testid={`sidebar-edit-${enc}`} className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 font-semibold" style={{ backgroundColor: "var(--arc-card)" }}><Settings2 size={13} /> Edit sidebar</button></div>
        </div>
        {EditDialog}
      </aside>
    );
  }

  return (
    <>
      {/* Desktop */}
      <aside className="group hidden w-56 shrink-0 lg:block">
        <div className="sticky top-20">
          {EditButton}
          <Items enc={enc} groups={cfg.groups} />
        </div>
      </aside>
      {/* Mobile */}
      <div className="lg:hidden">
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger data-testid="sidebar-mobile-toggle" className="flex w-full items-center justify-between rounded-md border px-4 py-3 text-sm font-semibold" style={{ backgroundColor: "var(--arc-card)" }}>
            <span className="flex items-center gap-2"><BookOpen size={16} /> Browse this archive</span>
            <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3 rounded-md border p-4" style={{ backgroundColor: "var(--arc-card)" }}>
            {isAdmin && <div className="mb-3">{<button onClick={() => setEditOpen(true)} className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold"><Settings2 size={13} /> Edit sidebar</button>}</div>}
            <Items enc={enc} groups={cfg.groups} />
          </CollapsibleContent>
        </Collapsible>
      </div>
      {EditDialog}
    </>
  );
}
